import { CollapsibleState } from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import { TreeNode } from "~/components/build/Show/routes/list/-components/TreeNode";
import { createJobRetryNode } from "~/components/build/Show/routes/list/-components/TreeNode/JobRetryItem";
import { CommandJob, RetryableJob } from "~/components/build/Show/lib/types";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import { pastAttemptsForJob } from "~/components/build/Show/lib/jobRetryHistory";
import { CommandStep } from "~/lib/pipeline";

/**
 * Child nodes for a job/command-step row's retry history: one row per past
 * attempt, oldest first. Read lazily from the store at flatten time so a
 * fetch-on-expand (or a retry-driven invalidation) refreshes the list.
 *
 * `referenceJob` is the latest attempt the history hangs off. For command
 * steps the row has no job object to hand, so it's derived from the most
 * recent job in the store (command steps are single-lane, so this is exact).
 */
export function retryHistoryChildNodes(
  step: CommandStep,
  referenceJob: RetryableJob | undefined,
  collapsibleState: CollapsibleState,
): TreeNode[] {
  if (collapsibleState === CollapsibleState.Collapsed) {
    return [];
  }

  const stepJobs = (useBuildPageStore.getState().stepJobs.get(step.uuid) || []) as RetryableJob[];
  if (stepJobs.length === 0) {
    return [];
  }

  const reference = referenceJob ?? stepJobs[stepJobs.length - 1];
  const attempts = pastAttemptsForJob(stepJobs, reference);

  // `attempts` is oldest-first, so the index gives the chronological attempt
  // number (#1 = oldest). Reverse the rendered rows so the most recent attempt
  // sits at the top, directly under the latest row it hangs off.
  return attempts
    .map((job, index) => createJobRetryNode(step, job as CommandJob, index + 1))
    .reverse();
}
